import { Component } from '@theme/component';

/**
 * Cross-fades through its slides on a timer.
 *
 * Deliberately not the theme's slideshow-component: that one owns a scroll
 * container, arrows, dots and a current index that has to stay in sync with
 * user input. Here the only way through the set is to wait, so there is
 * nothing to sync and nothing to control.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
class AutoRotator extends Component {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#show(0);

    // A single slide has nowhere to go
    if (this.#slides.length < 2) return;

    this.#start();
    this.addEventListener('mouseenter', this.#stop);
    this.addEventListener('mouseleave', this.#start);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    this.removeEventListener('mouseenter', this.#stop);
    this.removeEventListener('mouseleave', this.#start);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return this.refs.slides ?? [];
  }

  /**
   * Seconds between slides, from the interval attribute. Falls back to 3s when
   * the attribute is missing or not a number.
   *
   * @returns {number}
   */
  get #interval() {
    const seconds = Number.parseFloat(this.getAttribute('interval') ?? '');

    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 3000;
  }

  #start = () => {
    if (this.#timer) return;

    this.#timer = setInterval(() => this.#advance(), this.#interval);
  };

  #stop = () => {
    clearInterval(this.#timer);
    this.#timer = undefined;
  };

  #onVisibilityChange = () => {
    // No point burning through slides against a hidden tab
    if (document.hidden) this.#stop();
    else this.#start();
  };

  #advance() {
    this.#show((this.#index + 1) % this.#slides.length);
  }

  /** @param {number} index */
  #show(index) {
    this.#index = index;

    this.#slides.forEach((slide, position) => {
      const isCurrent = position === index;

      slide.classList.toggle('is-current', isCurrent);
      // Only the visible quote should reach assistive tech or the tab order
      slide.setAttribute('aria-hidden', String(!isCurrent));
      slide.toggleAttribute('inert', !isCurrent);
    });
  }
}

if (!customElements.get('auto-rotator')) {
  customElements.define('auto-rotator', AutoRotator);
}
